import {
  listPushSubscriptions,
  registerPushSubscription,
  revokePushSubscription,
  type PushSubscriptionRecord,
  type RegisterPushSubscriptionPayload,
  readActorUserIDFromAccessToken,
} from "../chats/chatApi";
import { connectRealtime, type RealtimeEvent, type RealtimeSubscription } from "../chats/realtimeClient";
import { readNotificationInstallMode, type NotificationInstallMode } from "./installMode";
import { navigateRuntimeToNotificationTarget } from "./notificationNavigationBridge";
import { buildNotificationTargetURL } from "./notificationLinks";

export { buildNotificationTargetURL } from "./notificationLinks";

const PROMPTED_STORAGE_KEY = "msg.notifications.prompted.v3";
const ENDPOINT_STORAGE_PREFIX = "msg.notifications.endpoint.v1";
const GENERIC_NOTIFICATION_BODY = "New message";
const NOTIFICATION_BODY_MAX_CHARACTERS = 160;
const NOTIFICATION_NAVIGATION_TAG_PREFIX = "msgnav:";
const DIAGNOSTICS_GLOBAL_KEY = "__msgNotificationDiagnostics";
const DIAGNOSTICS_MAX_ENTRIES = 120;
const PUSH_SUBSCRIPTION_RESYNC_INTERVAL_MILLIS = 6 * 60 * 60 * 1000;
const PUSH_SUBSCRIPTION_EXPIRATION_REFRESH_WINDOW_MILLIS = 60 * 60 * 1000;
const SERVICE_WORKER_READY_TIMEOUT_MILLIS = 5000;

export const NOTIFICATION_RUNTIME_REFRESH_EVENT_TYPE = "msg.notification.runtime.refresh";

let permissionRequestInFlight: Promise<NotificationPermission> | null = null;

interface NotificationDiagnosticEntry {
  timestamp: string;
  event: string;
  detail: Record<string, unknown>;
}

export type PermissionPromptDecision = "prompt" | "skip" | "sync";

export interface NotificationRuntimeOptions {
  actorUserID: string;
  accessToken: string;
}

export interface PushSubscriptionSyncPlan {
  shouldRegister: boolean;
  revokeEndpoints: string[];
}

interface PushSubscriptionSyncResult {
  ready: boolean;
  reason?: "service_worker_unavailable" | "local_subscription_unavailable";
}

export interface RealtimeNotificationDecisionInput {
  permission: NotificationPermission;
  documentHidden: boolean;
  eventType: string;
  iPhoneStandalone: boolean;
}

export interface NotificationRuntimeInstallState {
  standalone: boolean;
  iPhoneLike: boolean;
  requiresInstall: boolean;
  runtimeEligible: boolean;
}

export interface NotificationPermissionState {
  supported: boolean;
  permission: NotificationPermission | "unsupported";
}

function pushNotificationDiagnostic(event: string, detail: Record<string, unknown> = {}): void {
  const entry: NotificationDiagnosticEntry = {
    timestamp: new Date().toISOString(),
    event,
    detail,
  };

  if (typeof window !== "undefined") {
    try {
      const runtimeWindow = window as Window & {
        [DIAGNOSTICS_GLOBAL_KEY]?: NotificationDiagnosticEntry[];
      };
      const existing = Array.isArray(runtimeWindow[DIAGNOSTICS_GLOBAL_KEY])
        ? runtimeWindow[DIAGNOSTICS_GLOBAL_KEY]
        : [];
      existing.push(entry);
      runtimeWindow[DIAGNOSTICS_GLOBAL_KEY] = existing.slice(-DIAGNOSTICS_MAX_ENTRIES);
    } catch {
      // Ignore diagnostics storage failures.
    }
  }

  if (typeof console !== "undefined" && typeof console.info === "function") {
    console.info("[msg.notifications]", event, detail);
  }
}

function trimOrEmpty(value: string | null | undefined): string {
  if (typeof value !== "string") {
    return "";
  }
  return value.trim();
}

function normalizePositiveInt(value: number | null | undefined): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return null;
  }
  const normalized = Math.trunc(value);
  if (normalized <= 0) {
    return null;
  }
  return normalized;
}

function currentTimeMillis(): number {
  return Date.now();
}

function readPromptedFlag(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    return window.localStorage.getItem(PROMPTED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function setPromptedFlag(): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(PROMPTED_STORAGE_KEY, "true");
  } catch {
    // Ignore storage failures in private browsing modes.
  }
}

function endpointStorageKey(actorUserID: string): string {
  return `${ENDPOINT_STORAGE_PREFIX}:${actorUserID}`;
}

function readStoredEndpoint(actorUserID: string): string {
  if (typeof window === "undefined") {
    return "";
  }
  try {
    return trimOrEmpty(window.localStorage.getItem(endpointStorageKey(actorUserID)));
  } catch {
    return "";
  }
}

function writeStoredEndpoint(actorUserID: string, endpoint: string): void {
  if (typeof window === "undefined") {
    return;
  }
  const normalizedEndpoint = trimOrEmpty(endpoint);
  if (normalizedEndpoint === "") {
    return;
  }
  try {
    window.localStorage.setItem(endpointStorageKey(actorUserID), normalizedEndpoint);
  } catch {
    // Ignore storage failures.
  }
}

function decodeBase64URL(input: string): ArrayBuffer | null {
  const normalized = input.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
  try {
    const decoded = atob(padded);
    const bytes = new Uint8Array(decoded.length);
    for (let index = 0; index < decoded.length; index += 1) {
      bytes[index] = decoded.charCodeAt(index);
    }
    return bytes.buffer.slice(0);
  } catch {
    return null;
  }
}

function buildNotificationTitle(senderLabel: string, chatLabel: string): string {
  const sender = trimOrEmpty(senderLabel);
  const chat = trimOrEmpty(chatLabel);
  if (sender !== "" && chat !== "") {
    return `${sender} in ${chat}`;
  }
  if (sender !== "") {
    return sender;
  }
  if (chat !== "") {
    return chat;
  }
  return "New message";
}

function collapseWhitespace(value: string | null | undefined): string {
  const normalized = trimOrEmpty(value);
  if (normalized === "") {
    return "";
  }
  return normalized.replace(/\s+/g, " ").trim();
}

function truncateNotificationBody(value: string): string {
  if (value.length <= NOTIFICATION_BODY_MAX_CHARACTERS) {
    return value;
  }
  if (NOTIFICATION_BODY_MAX_CHARACTERS <= 3) {
    return value.slice(0, NOTIFICATION_BODY_MAX_CHARACTERS);
  }
  return `${value.slice(0, NOTIFICATION_BODY_MAX_CHARACTERS - 3)}...`;
}

function buildNotificationBodyPreview(body: string | null | undefined): string {
  const explicitBody = collapseWhitespace(body);
  if (explicitBody === "") {
    return GENERIC_NOTIFICATION_BODY;
  }
  return truncateNotificationBody(explicitBody);
}

function toPushSubscriptionPayload(
  subscription: PushSubscription | null,
): RegisterPushSubscriptionPayload | null {
  if (subscription === null) {
    return null;
  }

  const json = subscription.toJSON();
  const endpoint = trimOrEmpty(json.endpoint);
  const publicKey = trimOrEmpty(json.keys?.p256dh);
  const authToken = trimOrEmpty(json.keys?.auth);
  if (endpoint === "" || publicKey === "" || authToken === "") {
    return null;
  }

  return {
    endpoint,
    public_key: publicKey,
    auth_token: authToken,
  };
}

function hasMatchingSubscription(
  local: RegisterPushSubscriptionPayload,
  remote: PushSubscriptionRecord,
): boolean {
  return (
    trimOrEmpty(remote.endpoint) === local.endpoint &&
    trimOrEmpty(remote.public_key) === local.public_key &&
    trimOrEmpty(remote.auth_token) === local.auth_token
  );
}

export function resolvePermissionPromptDecision(
  permission: NotificationPermission,
  promptedAlready: boolean,
): PermissionPromptDecision {
  void promptedAlready;
  if (permission === "granted") {
    return "sync";
  }
  if (permission === "default") {
    return "prompt";
  }
  return "skip";
}

export function resolveInstallAwarePermissionPromptDecision(
  permission: NotificationPermission,
  promptedAlready: boolean,
  installMode: NotificationInstallMode,
): PermissionPromptDecision {
  if (permission === "granted") {
    return "sync";
  }
  if (!installMode.runtimeEligible || installMode.requiresInstall) {
    return "skip";
  }
  return resolvePermissionPromptDecision(permission, promptedAlready);
}

export function readNotificationRuntimeInstallState(): NotificationRuntimeInstallState {
  const installMode = readNotificationInstallMode();
  return {
    standalone: installMode.standalone,
    iPhoneLike: installMode.iPhoneLike,
    requiresInstall: installMode.requiresInstall,
    runtimeEligible: installMode.runtimeEligible,
  };
}

export function readNotificationPermissionState(): NotificationPermissionState {
  if (!isNotificationSupported()) {
    return {
      supported: false,
      permission: "unsupported",
    };
  }
  return {
    supported: true,
    permission: Notification.permission,
  };
}

export function refreshNotificationRuntime(): void {
  if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") {
    return;
  }
  window.dispatchEvent(new Event(NOTIFICATION_RUNTIME_REFRESH_EVENT_TYPE));
}

export async function requestNotificationPermissionFromUserGesture(): Promise<NotificationPermission> {
  const permission = await requestPermissionFromGesture();
  if (permission !== "default") {
    setPromptedFlag();
  }
  refreshNotificationRuntime();
  return permission;
}

export function planPushSubscriptionSync(
  local: RegisterPushSubscriptionPayload | null,
  remote: PushSubscriptionRecord[],
  previousEndpoint: string | null = null,
): PushSubscriptionSyncPlan {
  if (local === null) {
    return {
      shouldRegister: false,
      revokeEndpoints: [],
    };
  }

  const normalizedRemote = remote
    .map((subscription) => trimOrEmpty(subscription.endpoint))
    .filter((endpoint) => endpoint !== "");

  const shouldRegister = !remote.some((subscription) => hasMatchingSubscription(local, subscription));
  const normalizedPreviousEndpoint = trimOrEmpty(previousEndpoint);
  const revokeEndpoints =
    normalizedPreviousEndpoint !== "" &&
    normalizedPreviousEndpoint !== local.endpoint &&
    normalizedRemote.includes(normalizedPreviousEndpoint)
      ? [normalizedPreviousEndpoint]
      : [];
  return {
    shouldRegister,
    revokeEndpoints,
  };
}

function resolveRealtimeNotificationGateReason(input: RealtimeNotificationDecisionInput): string | null {
  if (input.permission !== "granted") {
    return "permission_not_granted";
  }
  if (input.eventType !== "message.created") {
    return "event_type_not_supported";
  }
  if (input.documentHidden) {
    return null;
  }
  return input.iPhoneStandalone ? null : "visible_non_standalone";
}

export function shouldDisplayRealtimeNotification(input: RealtimeNotificationDecisionInput): boolean {
  return resolveRealtimeNotificationGateReason(input) === null;
}

async function ensureServiceWorkerRegistration(source: "sync" | "notification"): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    pushNotificationDiagnostic("service_worker_unavailable", { source, reason: "service_worker_api_missing" });
    return null;
  }
  try {
    const registration = await navigator.serviceWorker.register("/sw.js");
    try {
      await registration.update();
      pushNotificationDiagnostic("service_worker_updated", { source });
    } catch {
      pushNotificationDiagnostic("service_worker_update_skipped", { source });
    }
    const readyRegistration = await waitForReadyServiceWorkerRegistration(registration);
    pushNotificationDiagnostic("service_worker_ready", { source });
    return readyRegistration;
  } catch {
    pushNotificationDiagnostic("service_worker_unavailable", { source, reason: "registration_failed" });
    return null;
  }
}

async function waitForReadyServiceWorkerRegistration(
  fallback: ServiceWorkerRegistration,
): Promise<ServiceWorkerRegistration> {
  if (
    typeof window === "undefined" ||
    typeof window.setTimeout !== "function" ||
    typeof navigator === "undefined" ||
    !("serviceWorker" in navigator)
  ) {
    return fallback;
  }

  const ready = navigator.serviceWorker.ready;
  if (typeof ready?.then !== "function") {
    return fallback;
  }

  let timeoutID: number | null = null;
  try {
    const timeout = new Promise<ServiceWorkerRegistration>((resolve) => {
      timeoutID = window.setTimeout(() => {
        pushNotificationDiagnostic("service_worker_ready_timeout", {});
        resolve(fallback);
      }, SERVICE_WORKER_READY_TIMEOUT_MILLIS);
    });
    return await Promise.race([ready, timeout]);
  } catch {
    return fallback;
  } finally {
    if (timeoutID !== null) {
      window.clearTimeout(timeoutID);
    }
  }
}

async function ensureLocalPushSubscription(
  registration: ServiceWorkerRegistration,
): Promise<RegisterPushSubscriptionPayload | null> {
  if (!("pushManager" in registration)) {
    return null;
  }

  let subscription = await registration.pushManager.getSubscription();
  if (subscription !== null && shouldRefreshPushSubscription(subscription)) {
    const previousEndpoint = trimOrEmpty(subscription.endpoint);
    try {
      const unsubscribed = await subscription.unsubscribe();
      pushNotificationDiagnostic("local_subscription_refresh", {
        reason: "expiring",
        unsubscribed,
        had_endpoint: previousEndpoint !== "",
      });
      subscription = unsubscribed ? null : await registration.pushManager.getSubscription();
    } catch {
      pushNotificationDiagnostic("local_subscription_refresh_failed", { reason: "unsubscribe_failed" });
    }
  }
  if (subscription === null) {
    const vapidKey = trimOrEmpty(import.meta.env.VITE_WEB_PUSH_PUBLIC_KEY);
    const applicationServerKey = decodeBase64URL(vapidKey);
    if (applicationServerKey === null) {
      return null;
    }
    try {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });
    } catch {
      return null;
    }
  }

  return toPushSubscriptionPayload(subscription);
}

function shouldRefreshPushSubscription(subscription: PushSubscription): boolean {
  const expirationTime = normalizePositiveInt(subscription.expirationTime);
  if (expirationTime === null) {
    return false;
  }
  return expirationTime - currentTimeMillis() <= PUSH_SUBSCRIPTION_EXPIRATION_REFRESH_WINDOW_MILLIS;
}

async function syncPushSubscription(actorUserID: string): Promise<PushSubscriptionSyncResult> {
  const registration = await ensureServiceWorkerRegistration("sync");
  if (registration === null) {
    pushNotificationDiagnostic("push_sync_skipped", { reason: "service_worker_unavailable" });
    return {
      ready: false,
      reason: "service_worker_unavailable",
    };
  }

  const local = await ensureLocalPushSubscription(registration);
  if (local === null) {
    pushNotificationDiagnostic("push_sync_skipped", { reason: "local_subscription_unavailable" });
    return {
      ready: false,
      reason: "local_subscription_unavailable",
    };
  }

  const listed = await listPushSubscriptions(actorUserID);
  const remote = Array.isArray(listed.subscriptions) ? listed.subscriptions : [];
  const previousEndpoint = readStoredEndpoint(actorUserID);
  const plan = planPushSubscriptionSync(local, remote, previousEndpoint);
  pushNotificationDiagnostic("push_sync_plan", {
    remote_count: remote.length,
    should_register: plan.shouldRegister,
    revoke_count: plan.revokeEndpoints.length,
  });

  for (const endpoint of plan.revokeEndpoints) {
    await revokePushSubscription(actorUserID, endpoint);
  }
  if (plan.shouldRegister) {
    await registerPushSubscription(actorUserID, local);
    pushNotificationDiagnostic("push_sync_registered", { endpoint_changed: previousEndpoint !== local.endpoint });
  }
  writeStoredEndpoint(actorUserID, local.endpoint);
  return {
    ready: true,
  };
}

function isNotificationSupported(): boolean {
  return typeof window !== "undefined" && "Notification" in window;
}

function isDocumentHidden(): boolean {
  if (typeof document === "undefined") {
    return false;
  }
  return document.visibilityState === "hidden" || document.hidden;
}

interface RealtimeNotificationPayload {
  title: string;
  targetURL: string;
  options: NotificationOptions;
}

function resolveRealtimeNotificationPayload(
  actorUserID: string,
  event: RealtimeEvent,
): RealtimeNotificationPayload | null {
  const sender = trimOrEmpty(event.sender_user_id);
  if (sender !== "" && sender === actorUserID) {
    pushNotificationDiagnostic("event_suppressed", { reason: "sender_is_actor" });
    return null;
  }
  const chatID = trimOrEmpty(event.chat_id);
  if (chatID === "") {
    pushNotificationDiagnostic("event_suppressed", { reason: "missing_chat_id" });
    return null;
  }

  const title = buildNotificationTitle(sender || "Message", chatID);
  const normalizedMessageID = trimOrEmpty(event.message_id);
  const normalizedSequence = normalizePositiveInt(event.sequence);
  const targetURL = buildNotificationTargetURL({
    conversationID: chatID,
    aroundSequence: event.sequence,
    messageID: normalizedMessageID,
  });
  const tagParts = [`${NOTIFICATION_NAVIGATION_TAG_PREFIX}${encodeURIComponent(chatID)}`];
  if (normalizedMessageID !== "") {
    tagParts.push(encodeURIComponent(normalizedMessageID));
  }
  if (normalizedSequence !== null) {
    tagParts.push(String(normalizedSequence));
  }

  return {
    title,
    targetURL,
    options: {
      body: buildNotificationBodyPreview(event.body),
      data: {
        target_url: targetURL,
        conversation_id: chatID,
        around_sequence: normalizedSequence,
        message_id: normalizedMessageID,
      },
      tag: tagParts.join("|"),
    },
  };
}

async function showRealtimeNotification(actorUserID: string, event: RealtimeEvent): Promise<void> {
  const payload = resolveRealtimeNotificationPayload(actorUserID, event);
  if (payload === null) {
    return;
  }

  const registration = await ensureServiceWorkerRegistration("notification");
  if (registration !== null && typeof registration.showNotification === "function") {
    try {
      await registration.showNotification(payload.title, payload.options);
      pushNotificationDiagnostic("notification_displayed", { channel: "service_worker" });
      return;
    } catch (error) {
      pushNotificationDiagnostic("notification_service_worker_failed", {
        error: error instanceof Error ? error.name : "unknown_error",
      });
    }
  }

  try {
    const notification = new Notification(payload.title, payload.options);
    notification.onclick = () => {
      pushNotificationDiagnostic("notification_clicked", { channel: "window_notification_api" });
      if (typeof window === "undefined") {
        return;
      }
      try {
        window.focus();
      } catch {
        // Ignore focus failures and continue with navigation attempt.
      }
      if (payload.targetURL !== "") {
        navigateRuntimeToNotificationTarget(window, payload.targetURL);
      }
    };
    notification.onerror = () => {};
    pushNotificationDiagnostic("notification_displayed", { channel: "window_notification_api" });
    return;
  } catch (error) {
    pushNotificationDiagnostic("notification_window_failed", {
      error: error instanceof Error ? error.name : "unknown_error",
    });
  }
}

async function requestPermissionFromGesture(): Promise<NotificationPermission> {
  if (!isNotificationSupported()) {
    return "denied";
  }
  if (permissionRequestInFlight !== null) {
    return permissionRequestInFlight;
  }
  permissionRequestInFlight = (async () => {
    try {
      return await Notification.requestPermission();
    } catch {
      return "default";
    } finally {
      permissionRequestInFlight = null;
    }
  })();
  return permissionRequestInFlight;
}

function subscribeToRuntimeContextChanges(onChange: () => void): () => void {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return () => {};
  }

  const displayModeQuery =
    typeof window.matchMedia === "function" ? window.matchMedia("(display-mode: standalone)") : null;

  window.addEventListener("focus", onChange);
  window.addEventListener(NOTIFICATION_RUNTIME_REFRESH_EVENT_TYPE, onChange);
  document.addEventListener("visibilitychange", onChange);

  if (displayModeQuery !== null) {
    if (typeof displayModeQuery.addEventListener === "function") {
      displayModeQuery.addEventListener("change", onChange);
    } else if (typeof displayModeQuery.addListener === "function") {
      displayModeQuery.addListener(onChange);
    }
  }

  return () => {
    window.removeEventListener("focus", onChange);
    window.removeEventListener(NOTIFICATION_RUNTIME_REFRESH_EVENT_TYPE, onChange);
    document.removeEventListener("visibilitychange", onChange);

    if (displayModeQuery !== null) {
      if (typeof displayModeQuery.removeEventListener === "function") {
        displayModeQuery.removeEventListener("change", onChange);
      } else if (typeof displayModeQuery.removeListener === "function") {
        displayModeQuery.removeListener(onChange);
      }
    }
  };
}

function isActiveSession(options: NotificationRuntimeOptions): boolean {
  const actorUserID = trimOrEmpty(options.actorUserID);
  const accessToken = trimOrEmpty(options.accessToken);
  if (actorUserID === "" || accessToken === "") {
    return false;
  }
  return readActorUserIDFromAccessToken(accessToken) === actorUserID;
}

export function startNotificationRuntime(options: NotificationRuntimeOptions): () => void {
  if (!isActiveSession(options) || !isNotificationSupported()) {
    pushNotificationDiagnostic("runtime_not_started", {
      reason: isNotificationSupported() ? "inactive_session" : "notification_api_unsupported",
    });
    return () => {};
  }

  const actorUserID = trimOrEmpty(options.actorUserID);
  const accessToken = trimOrEmpty(options.accessToken);
  let installMode = readNotificationInstallMode();
  let stopped = false;
  let realtimeSubscription: RealtimeSubscription | null = null;
  let removePromptListeners: (() => void) | null = null;
  let promptInFlight = false;
  let syncedGrantedRuntime = false;
  let pushSyncInFlight = false;
  let lastPushSyncAttemptMillis = 0;
  let resyncTimer: number | null = null;
  pushNotificationDiagnostic("runtime_started", {
    permission: Notification.permission,
    standalone: installMode.standalone,
    requires_install: installMode.requiresInstall,
    runtime_eligible: installMode.runtimeEligible,
  });

  const stopRealtime = () => {
    if (realtimeSubscription !== null) {
      pushNotificationDiagnostic("realtime_stopped", {});
      realtimeSubscription.close();
      realtimeSubscription = null;
    }
  };

  const maybeStartRealtime = () => {
    if (stopped || realtimeSubscription !== null) {
      return;
    }

    realtimeSubscription = connectRealtime({
      accessToken,
      onEvent: (event) => {
        const eventInstallMode = readNotificationInstallMode();
        const decisionInput = {
          permission: Notification.permission,
          documentHidden: isDocumentHidden(),
          eventType: event.type,
          iPhoneStandalone: eventInstallMode.iPhoneLike && eventInstallMode.standalone,
        };
        const gateReason = resolveRealtimeNotificationGateReason(decisionInput);
        if (gateReason !== null) {
          pushNotificationDiagnostic("event_suppressed", {
            reason: gateReason,
            permission: decisionInput.permission,
            document_hidden: decisionInput.documentHidden,
            iPhone_standalone: decisionInput.iPhoneStandalone,
            event_type: decisionInput.eventType,
          });
          return;
        }
        void showRealtimeNotification(actorUserID, event);
      },
    });
    pushNotificationDiagnostic("realtime_started", {});
  };

  const ensureGrantedRuntime = async () => {
    if (stopped) {
      return;
    }
    if (Notification.permission !== "granted") {
      syncedGrantedRuntime = false;
      return;
    }
    const shouldSync =
      !syncedGrantedRuntime ||
      lastPushSyncAttemptMillis === 0 ||
      currentTimeMillis() - lastPushSyncAttemptMillis >= PUSH_SUBSCRIPTION_RESYNC_INTERVAL_MILLIS;
    if (shouldSync && !pushSyncInFlight) {
      pushSyncInFlight = true;
      lastPushSyncAttemptMillis = currentTimeMillis();
      try {
        const syncResult = await syncPushSubscription(actorUserID);
        syncedGrantedRuntime = syncResult.ready;
        if (syncResult.ready) {
          pushNotificationDiagnostic("push_sync_success", {});
        } else {
          pushNotificationDiagnostic("push_sync_pending", {
            reason: syncResult.reason ?? "unknown",
          });
        }
      } catch (error) {
        syncedGrantedRuntime = false;
        // Keep runtime resilient if backend sync temporarily fails.
        pushNotificationDiagnostic("push_sync_failed", {
          error: error instanceof Error ? error.name : "unknown_error",
        });
      } finally {
        pushSyncInFlight = false;
      }
    }
    maybeStartRealtime();
  };

  const clearPromptListeners = () => {
    removePromptListeners?.();
    removePromptListeners = null;
  };

  const triggerPermissionPrompt = () => {
    if (stopped || promptInFlight) {
      return;
    }
    promptInFlight = true;
    pushNotificationDiagnostic("permission_prompt_requested", { via: "gesture" });
    void (async () => {
      const permission = await requestNotificationPermissionFromUserGesture();
      pushNotificationDiagnostic("permission_prompt_resolved", { permission });
      if (!stopped && permission === "granted") {
        await ensureGrantedRuntime();
      }
      promptInFlight = false;
      if (!stopped && permission === "default") {
        maybeRefreshRuntime();
      }
    })();
  };

  const ensurePromptListeners = () => {
    if (removePromptListeners !== null || stopped) {
      return;
    }
    pushNotificationDiagnostic("gesture_listeners_armed", {});

    const onGesture = () => {
      clearPromptListeners();
      triggerPermissionPrompt();
    };

    const keydownListener = () => {
      onGesture();
    };
    const pointerListener = () => {
      onGesture();
    };
    const touchListener = () => {
      onGesture();
    };
    const clickListener = () => {
      onGesture();
    };

    window.addEventListener("keydown", keydownListener, { capture: true });
    window.addEventListener("pointerdown", pointerListener, { capture: true });
    window.addEventListener("touchend", touchListener, { capture: true });
    window.addEventListener("click", clickListener, { capture: true });

    removePromptListeners = () => {
      window.removeEventListener("keydown", keydownListener, { capture: true });
      window.removeEventListener("pointerdown", pointerListener, { capture: true });
      window.removeEventListener("touchend", touchListener, { capture: true });
      window.removeEventListener("click", clickListener, { capture: true });
    };
  };

  const maybeRefreshRuntime = () => {
    installMode = readNotificationInstallMode();
    const decision = resolveInstallAwarePermissionPromptDecision(
      Notification.permission,
      readPromptedFlag(),
      installMode,
    );
    pushNotificationDiagnostic("runtime_refreshed", {
      decision,
      permission: Notification.permission,
      standalone: installMode.standalone,
      requires_install: installMode.requiresInstall,
      runtime_eligible: installMode.runtimeEligible,
    });

    if (decision === "sync") {
      clearPromptListeners();
      void ensureGrantedRuntime();
      return;
    }

    stopRealtime();
    syncedGrantedRuntime = false;

    if (decision === "prompt") {
      ensurePromptListeners();
      return;
    }

    clearPromptListeners();
  };

  const removeRuntimeContextListeners = subscribeToRuntimeContextChanges(() => {
    if (!stopped) {
      maybeRefreshRuntime();
    }
  });
  if (typeof window !== "undefined" && typeof window.setInterval === "function") {
    resyncTimer = window.setInterval(() => {
      if (!stopped) {
        void ensureGrantedRuntime();
      }
    }, PUSH_SUBSCRIPTION_RESYNC_INTERVAL_MILLIS);
  }
  maybeRefreshRuntime();

  return () => {
    stopped = true;
    if (resyncTimer !== null) {
      window.clearInterval(resyncTimer);
      resyncTimer = null;
    }
    removeRuntimeContextListeners();
    clearPromptListeners();
    stopRealtime();
    pushNotificationDiagnostic("runtime_stopped", {});
  };
}
